<template>
  <div class="code-editor-wrapper">
    <div class="code-editor-toolbar" v-if="showToolbar">
      <div class="toolbar-left">
        <el-select
          v-model="currentLanguage"
          @change="changeLanguage"
          size="mini"
          placeholder="选择语言"
          style="width: 120px;">
          <el-option
            v-for="lang in supportedLanguages"
            :key="lang.value"
            :label="lang.label"
            :value="lang.value">
          </el-option>
        </el-select>

        <el-select
          v-model="currentTheme"
          @change="changeTheme"
          size="mini"
          placeholder="选择主题"
          style="width: 120px; margin-left: 10px;">
          <el-option
            v-for="theme in themes"
            :key="theme.value"
            :label="theme.label"
            :value="theme.value">
          </el-option>
        </el-select>
      </div>

      <div class="toolbar-right">
        <el-button @click="formatCode" size="mini" type="primary">
          <i class="el-icon-magic-stick"></i> 格式化
        </el-button>
        <el-button @click="copyCode" size="mini">
          <i class="el-icon-document-copy"></i> 复制
        </el-button>
        <el-button @click="clearCode" size="mini">
          <i class="el-icon-delete"></i> 清空
        </el-button>
      </div>
    </div>

    <codemirror
      ref="myCodeMirror"
      v-model="content"
      :options="editorOptions"
      @ready="onEditorReady"
      @focus="onEditorFocus"
      @input="onEditorInput"
      class="code-editor">
    </codemirror>
  </div>
</template>

<script>
import { codemirror } from 'vue-codemirror'

// 导入 CodeMirror 样式
import 'codemirror/lib/codemirror.css'

// 导入主题
import 'codemirror/theme/material.css'
import 'codemirror/theme/dracula.css'
import 'codemirror/theme/monokai.css'
import 'codemirror/theme/eclipse.css'
import 'codemirror/theme/solarized.css'

// 导入语言模式
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/python/python.js'
import 'codemirror/mode/clike/clike.js' // Java, C++, C#
import 'codemirror/mode/sql/sql.js'
import 'codemirror/mode/xml/xml.js' // HTML, XML
import 'codemirror/mode/css/css.js'
import 'codemirror/mode/vue/vue.js'
import 'codemirror/mode/markdown/markdown.js'
import 'codemirror/mode/shell/shell.js'
import 'codemirror/mode/yaml/yaml.js'
import 'codemirror/mode/dockerfile/dockerfile.js'

// 导入编辑器功能
import 'codemirror/addon/edit/closebrackets.js'
import 'codemirror/addon/edit/matchbrackets.js'
import 'codemirror/addon/selection/active-line.js'
import 'codemirror/addon/fold/foldcode.js'
import 'codemirror/addon/fold/foldgutter.js'
import 'codemirror/addon/fold/brace-fold.js'
import 'codemirror/addon/fold/comment-fold.js'
import 'codemirror/addon/fold/foldgutter.css'

export default {
  name: 'CodeEditor',
  components: {
    codemirror
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'javascript'
    },
    theme: {
      type: String,
      default: 'default'
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    height: {
      type: String,
      default: '300px'
    },
    placeholder: {
      type: String,
      default: '请输入代码...'
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      content: this.value,
      currentLanguage: this.language,
      currentTheme: this.theme,
      supportedLanguages: [
        { label: 'JavaScript', value: 'javascript' },
        { label: 'Python', value: 'python' },
        { label: 'Java', value: 'text/x-java' },
        { label: 'C++', value: 'text/x-c++src' },
        { label: 'C#', value: 'text/x-csharp' },
        { label: 'SQL', value: 'sql' },
        { label: 'HTML', value: 'xml' },
        { label: 'CSS', value: 'css' },
        { label: 'Vue', value: 'vue' },
        { label: 'Markdown', value: 'markdown' },
        { label: 'Shell', value: 'shell' },
        { label: 'YAML', value: 'yaml' },
        { label: 'Dockerfile', value: 'dockerfile' },
        { label: 'Text', value: 'text' }
      ],
      themes: [
        { label: '默认', value: 'default' },
        { label: 'Material', value: 'material' },
        { label: 'Dracula', value: 'dracula' },
        { label: 'Monokai', value: 'monokai' },
        { label: 'Eclipse', value: 'eclipse' },
        { label: 'Solarized Dark', value: 'solarized dark' },
        { label: 'Solarized Light', value: 'solarized light' }
      ]
    }
  },
  computed: {
    editorOptions() {
      return {
        tabSize: 2,
        mode: this.currentLanguage,
        theme: this.currentTheme,
        lineNumbers: true,
        line: true,
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
        styleActiveLine: true,
        matchBrackets: true,
        autoCloseBrackets: true,
        readOnly: this.readOnly,
        placeholder: this.placeholder,
        extraKeys: {
          'Ctrl-/': 'toggleComment',
          'Cmd-/': 'toggleComment',
          'Ctrl-F': 'findPersistent',
          'Cmd-F': 'findPersistent',
          'Ctrl-A': 'selectAll',
          'Cmd-A': 'selectAll'
        }
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.content) {
        this.content = newVal
      }
    },
    content(newVal) {
      this.$emit('input', newVal)
    },
    language(newVal) {
      this.currentLanguage = newVal
    },
    theme(newVal) {
      this.currentTheme = newVal
    }
  },
  mounted() {
    // 设置编辑器高度
    this.$nextTick(() => {
      if (this.$refs.myCodeMirror) {
        this.$refs.myCodeMirror.codemirror.setSize(null, this.height)
      }
    })
  },
  methods: {
    onEditorReady(cm) {
      cm.setSize(null, this.height)
    },
    onEditorFocus(cm) {
      this.$emit('focus', cm)
    },
    onEditorInput(value) {
      this.$emit('input', value)
      this.$emit('change', value)
    },
    changeLanguage(language) {
      this.currentLanguage = language
      this.$emit('language-change', language)
    },
    changeTheme(theme) {
      this.currentTheme = theme
      this.$emit('theme-change', theme)
    },
    formatCode() {
      // 简单的代码格式化（主要是缩进）
      const cm = this.$refs.myCodeMirror.codemirror
      const totalLines = cm.lineCount()
      cm.operation(() => {
        for (let i = 0; i < totalLines; i++) {
          cm.indentLine(i)
        }
      })
      this.$message.success('代码格式化完成')
    },
    copyCode() {
      const code = this.content
      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).then(() => {
          this.$message.success('代码已复制到剪贴板')
        }).catch(() => {
          this.fallbackCopyToClipboard(code)
        })
      } else {
        this.fallbackCopyToClipboard(code)
      }
    },
    fallbackCopyToClipboard(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
        this.$message.success('代码已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(textArea)
    },
    clearCode() {
      this.$confirm('确定要清空所有代码吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.content = ''
        this.$message.success('代码已清空')
      }).catch(() => {})
    },
    insertText(text) {
      const cm = this.$refs.myCodeMirror.codemirror
      cm.replaceSelection(text)
    },
    getSelectedText() {
      const cm = this.$refs.myCodeMirror.codemirror
      return cm.getSelection()
    },
    setCursor(line, ch) {
      const cm = this.$refs.myCodeMirror.codemirror
      cm.setCursor(line, ch)
    },
    focus() {
      const cm = this.$refs.myCodeMirror.codemirror
      cm.focus()
    }
  }
}
</script>

<style scoped lang="less">
.code-editor-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;

  .code-editor-toolbar {
    background: #f5f7fa;
    padding: 8px 12px;
    border-bottom: 1px solid #dcdfe6;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .toolbar-left {
      display: flex;
      align-items: center;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .code-editor {
    /deep/ .CodeMirror {
      border: none;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 14px;
      line-height: 1.5;
    }

    /deep/ .CodeMirror-selected {
      background: #b3d4fc !important;
    }

    /deep/ .CodeMirror-focused .CodeMirror-selected {
      background: #b3d4fc !important;
    }

    /deep/ .CodeMirror-line::selection,
    /deep/ .CodeMirror-line > span::selection,
    /deep/ .CodeMirror-line > span > span::selection {
      background: #b3d4fc;
    }
  }
}

// 深色主题样式调整
.code-editor-wrapper {
  /deep/ .cm-s-material .CodeMirror-gutters,
  /deep/ .cm-s-dracula .CodeMirror-gutters,
  /deep/ .cm-s-monokai .CodeMirror-gutters {
    border-right: 1px solid rgba(255, 255, 255, 0.1);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CodeEditor.vue